import React, { useState, useEffect, useRef } from "react";
import Section from "./layouts/Section";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Redux and utilities
import { authActions } from '../redux/actions';
import { userSelect, allRemoteSettingsSelect, originalFinanceSelect } from '../redux/selectors/index';
import { notify, money } from '../utility/Helper1';
import { staticCountries } from '../utility/countries';
import { SiteWide, CountrySelector } from "./customComponents";

// MUI icons
import PersonIcon from "@mui/icons-material/Person";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import NotificationsIcon from "@mui/icons-material/Notifications";
import EditIcon from "@mui/icons-material/Edit";
import VerifiedIcon from "@mui/icons-material/Verified";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import BoltIcon from "@mui/icons-material/Bolt";
import StarIcon from "@mui/icons-material/Star";
import WifiIcon from "@mui/icons-material/Wifi";
import DiamondIcon from "@mui/icons-material/Diamond";
import LockIcon from "@mui/icons-material/Lock";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PublicIcon from "@mui/icons-material/Public";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import KeyIcon from "@mui/icons-material/Key";
import ShieldIcon from "@mui/icons-material/Shield";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

import { Avatar, Backdrop, CircularProgress } from "@mui/material";
import {
    RiFacebookFill,
    RiWhatsappFill,
    RiTelegramFill,
    RiTwitterXFill,
    RiInstagramFill,
    RiYoutubeFill,
    RiLinkedinFill,
    RiTiktokFill
} from "react-icons/ri";

function PersonalInformation() {
    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);

    const socialPlatforms = [
        { name: 'Facebook', key: 'facebook', icon: RiFacebookFill, color: '#1877F2' },
        { name: 'WhatsApp', key: 'whatsapp', icon: RiWhatsappFill, color: '#25D366' },
        { name: 'Telegram', key: 'telegram', icon: RiTelegramFill, color: '#0088CC' },
        { name: 'Twitter', key: 'twitter', icon: RiTwitterXFill, color: '#1DA1F2' },
        { name: 'Instagram', key: 'instagram', icon: RiInstagramFill, color: '#E1306C' },
        { name: 'YouTube', key: 'youtube', icon: RiYoutubeFill, color: '#FF0000' },
        { name: 'LinkedIn', key: 'linkedin', icon: RiLinkedinFill, color: '#0077B5' },
        { name: 'TikTok', key: 'tiktok', icon: RiTiktokFill, color: '#010101' },
    ];

    const activeSocials = socialPlatforms.filter(platform => {
        const url = site_settings?.site_settings?.[platform.key];
        return url && url.trim() !== '';
    });
    const finance = useSelector(originalFinanceSelect);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const hiddenFileInput = useRef(null);

    const [passwordModal, setPasswordModal] = useState(false);
    const [phoneModal, setPhoneModal] = useState(false);

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [withdrawalPin, setWithdrawalPin] = useState('');
    const [phone, setPhone] = useState(userData?.phone ?? '');
    const [country, setCountry] = useState(userData?.country ?? '');
    const [phoneCode, setPhoneCode] = useState('...');

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showPin, setShowPin] = useState(false);
    const [countryFocused, setCountryFocused] = useState(false);

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (!password || !confirmPassword) { notify('Please fill in all password fields', 0); return; }
        if (password !== confirmPassword) { notify('Passwords do not match', 0); return; }
        if (password.length < 8) { notify('Password must be at least 8 characters long', 0); return; }
        if (!withdrawalPin) { notify('Please enter your PIN', 0); return; }
        setLoading(true);
        setLoadingText('Updating password...');
        dispatch(authActions.onLogin("account/change_password", { password, pin: withdrawalPin }, response => {
            if (response.code === 1) { setPassword(''); setConfirmPassword(''); setWithdrawalPin(''); setPasswordModal(false); }
            notify(response.msg, response.code);
            setLoading(false);
        }));
    };

    const handlePhoneChange = async (e) => {
        e.preventDefault();
        if (!phone || !country) { notify('Please fill in all phone fields', 0); return; }
        if (!withdrawalPin) { notify('Please enter your PIN', 0); return; }
        setLoading(true);
        setLoadingText('Updating phone number...');
        dispatch(authActions.onLogin("account/update_phone", { country, phone, pin: withdrawalPin }, response => {
            if (response.code === 1) { setWithdrawalPin(''); setPhoneModal(false); }
            notify(response.msg, response.code);
            setLoading(false);
        }));
    };

    const handleCountryChange = (countryId) => {
        const selectedCountry = staticCountries.find(e => e.id == countryId);
        setCountry(countryId);
        setPhoneCode(selectedCountry?.phonecode || '...');
    };

    const progClickFileUpload = () => { hiddenFileInput.current?.click(); };

    const uploadAvatar = async (file) => {
        setUploadingAvatar(true);
        dispatch(authActions.onLogin("account/change-avatar", { avatar: file }, response => { setUploadingAvatar(false); }));
    };

    const calculateProfileCompleteness = () => {
        let score = 0;
        const total = 6;
        if (userData?.email) score++;
        if (userData?.phone) score++;
        if (userData?.country) score++;
        if (userData?.bank != '') score++;
        if (userData?.usdtbep20wallet != '') score++;
        if (userData?.emailVerification === 1) score++;
        return Math.round((score / total) * 100);
    };

    useEffect(() => { handleCountryChange(country); }, []);

    useEffect(() => {
        if (userData?.emailVerification === 1) {
            const timing = setTimeout(() => {
                dispatch(authActions.onGetHomePageData("user/load/homepage", response => { }));
            }, 1000);
            return () => clearTimeout(timing);
        }
    }, [userData?.emailVerification, dispatch]);

    const profileCompleteness = calculateProfileCompleteness();

    const accountInfoItems = [
        { icon: <AccountCircleIcon style={{ fontSize: 18 }} />, label: 'Username', value: userData?.username || 'Not set', verified: !!userData?.username, color: "#3b82f6" },
        { icon: <EmailIcon style={{ fontSize: 18 }} />, label: 'Email Address', value: userData?.email || 'Not verified', verified: userData?.emailVerification === 1, color: "#22c55e" },
        { icon: <PhoneIcon style={{ fontSize: 18 }} />, label: 'Phone Number', value: userData?.phone || 'Not set', verified: !!userData?.phone, color: "#f59e0b" },
        { icon: <PublicIcon style={{ fontSize: 18 }} />, label: 'Country', value: userData?.countryName || 'Not set', verified: !!userData?.countryName, color: "#a855f7" },
    ];

    const securityItems = [
        { icon: <KeyIcon style={{ fontSize: 18 }} />, label: 'Change Password', sub: 'Update your account password', action: () => navigate('/change-password'), color: "#3b82f6" },
        { icon: <PhoneIcon style={{ fontSize: 18 }} />, label: 'Update Phone Number', sub: 'Modify your registered phone', action: () => navigate('/phone'), color: "#22c55e" },
        { icon: <ShieldIcon style={{ fontSize: 18 }} />, label: 'Change PIN', sub: 'Update your withdrawal PIN', action: () => navigate('/change-pin'), color: "#f59e0b" },
    ];

    const historyItems = [
        { icon: <ReceiptLongIcon style={{ fontSize: 18 }} />, label: 'Deposit History', sub: 'View all deposit transactions', action: () => navigate('/Deposit-records'), color: "#3b82f6" },
        { icon: <ArrowCircleDownIcon style={{ fontSize: 18 }} />, label: 'Withdrawal History', sub: 'Review past withdrawals', action: () => navigate('/withdrawal-records'), color: "#a855f7" },
    ];

    const badges = [
        { label: "Power Chatter", icon: <BoltIcon style={{ fontSize: 14 }} />, color: "#3b82f6" },
        { label: "Top Earner", icon: <StarIcon style={{ fontSize: 14 }} />, color: "#f59e0b" },
        { label: "Connector", icon: <WifiIcon style={{ fontSize: 14 }} />, color: "#22c55e" },
        { label: "Diamond", icon: <DiamondIcon style={{ fontSize: 14 }} />, color: "#a855f7" },
    ];

    const inputStyle = {
        width: "100%", borderRadius: 12, padding: "12px 14px",
        border: "1px solid var(--border-color)",
        background: "var(--neutral-100)", color: "var(--text-main)",
        fontSize: 14, fontFamily: "'DM Sans',sans-serif",
        outline: "none", boxSizing: "border-box"
    };

    return (
        <SiteWide id="main" title="Profile">
            <Section loading={loading} overlayText={loadingText} title="Profile">
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                    {/* Profile card */}
                    <div style={{
                        background: "#ffffff", border: "1px solid rgba(59,130,246,0.08)",
                        boxShadow: "0 4px 18px rgba(59,130,246,0.04)", borderRadius: 16, overflow: "hidden"
                    }}>
                        {/* Cover banner */}
                        <div style={{
                            height: 80, position: "relative",
                            background: "linear-gradient(135deg,#1d4ed8,#4c1d95)"
                        }}>
                            <div style={{
                                position: "absolute", inset: 0,
                                backgroundImage: "radial-gradient(circle at 30% 50%,rgba(96,165,250,0.3) 0%,transparent 60%)"
                            }} />
                        </div>

                        <div style={{ padding: "0 16px 16px" }}>
                            {/* Avatar */}
                            <div style={{ position: "relative", width: "fit-content", marginTop: -36, marginBottom: 10 }}>
                                <div style={{ position: "relative" }}>
                                    {uploadingAvatar && (
                                        <Backdrop open sx={{ position: 'absolute', zIndex: 5, borderRadius: '50%' }}>
                                            <CircularProgress size={14} sx={{ color: '#3b82f6' }} />
                                        </Backdrop>
                                    )}
                                    <Avatar
                                        onClick={progClickFileUpload}
                                        src={userData?.image || undefined}
                                        sx={{
                                            width: 68, height: 68, cursor: 'pointer',
                                            border: '3px solid #ffffff',
                                            boxShadow: '0 8px 24px rgba(59,130,246,0.2)',
                                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                            fontSize: '1.5rem', fontWeight: 'bold', color: '#fff',
                                            fontFamily: "'Syne',sans-serif"
                                        }}
                                    >
                                        {!userData?.image && userData?.username?.charAt(0)?.toUpperCase()}
                                    </Avatar>
                                    <button
                                        onClick={progClickFileUpload}
                                        style={{
                                            position: "absolute", bottom: 0, right: 0,
                                            width: 22, height: 22, borderRadius: 6,
                                            background: "#3b82f6", border: "2px solid #ffffff",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            cursor: "pointer"
                                        }}
                                    >
                                        <CameraAltIcon style={{ color: "#fff", fontSize: 11 }} />
                                    </button>
                                </div>
                                <input ref={hiddenFileInput} type="file" accept=".jpg,.gif,.png,.jpeg"
                                    onChange={(e) => uploadAvatar(e.target.files[0])} style={{ display: 'none' }} />
                            </div>

                            {/* Name and username */}
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                                <h3 style={{ color: "#0f172a", fontSize: 18, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: 0 }}>
                                    {userData?.username || 'Your Name'}
                                </h3>
                                {userData?.emailVerification === 1 && <VerifiedIcon style={{ color: "#3b82f6", fontSize: 16 }} />}
                            </div>
                            <p style={{ color: "#64748b", fontSize: 12, fontFamily: "'DM Sans',sans-serif", marginBottom: 10 }}>
                                {userData?.email}
                            </p>

                        </div>
                    </div>

                    {/* Active Profile Switcher */}
                    <div>
                        <p style={{ color: "#64748b", fontSize: 12, fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, margin: "0 0 10px" }}>
                            Active Profile
                        </p>
                        <div style={{
                            background: "#ffffff", border: "1px solid rgba(59,130,246,0.08)",
                            boxShadow: "0 4px 18px rgba(59,130,246,0.04)", borderRadius: 16,
                            padding: 16, display: "flex", flexDirection: "column", gap: 10
                        }}>
                            <p style={{ color: "#64748b", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: "0 0 4px", lineHeight: 1.5 }}>
                                Switch your profile to manage activities in your preferred currency.
                            </p>
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                {finance?.map((cur) => {
                                    const isActive = localStorage.getItem('activeCurrencyType')
                                        ? String(cur.type) === String(localStorage.getItem('activeCurrencyType'))
                                        : String(cur.type) === String(finance?.[0]?.type);

                                    return (
                                        <button
                                            key={cur.type}
                                            onClick={() => {
                                                localStorage.setItem('activeCurrencyType', String(cur.type));
                                                // Trigger system data refresh to update all components in Redux
                                                dispatch(authActions.onRefreshSystemData("system/refresh", 10, {}, () => { }));
                                                notify(`Switched to ${cur.name} Profile`, 1);
                                            }}
                                            style={{
                                                flex: "1 1 140px", display: "flex", alignItems: "center", gap: 10,
                                                padding: "12px 14px", borderRadius: 14, cursor: "pointer", outline: "none",
                                                background: isActive ? "linear-gradient(135deg,#2563eb,#1d4ed8)" : "var(--neutral-100)",
                                                border: isActive ? "none" : "1.5px solid var(--border-color)",
                                                boxShadow: isActive ? "0 4px 12px rgba(37,99,235,0.2)" : "none",
                                                color: isActive ? "#ffffff" : "var(--text-main)",
                                                transition: "all 0.2s ease",
                                                textAlign: "left"
                                            }}
                                        >

                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {cur.name}
                                                </p>
                                                <p style={{ margin: 0, fontSize: 10, color: isActive ? "rgba(255,255,255,0.75)" : "var(--text-muted)" }}>
                                                    {money(Number(cur.balance) + Number(cur.commissionRO), cur.type)}
                                                </p>
                                            </div>
                                            {isActive && (
                                                <CheckCircleIcon style={{ color: "#ffffff", fontSize: 16 }} />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Account Information */}
                    <div>
                        <p style={{ color: "#64748b", fontSize: 12, fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, margin: "0 0 10px" }}>
                            Account Information
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {accountInfoItems.map((item, i) => (
                                <div key={i} style={{
                                    background: "#ffffff", border: "1px solid rgba(59,130,246,0.08)",
                                    boxShadow: "0 2px 12px rgba(59,130,246,0.02)", borderRadius: 14,
                                    padding: "12px 14px", display: "flex", alignItems: "center", gap: 12
                                }}>
                                    <div style={{
                                        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                                        background: `${item.color}0a`, border: `1px solid ${item.color}1e`,
                                        color: item.color, display: "flex", alignItems: "center", justifyContent: "center"
                                    }}>
                                        {item.icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <p style={{ color: "#94a3b8", fontSize: 10, fontFamily: "'DM Sans',sans-serif", margin: 0, textTransform: "uppercase", letterSpacing: "0.04em" }}>{item.label}</p>
                                        <p style={{ color: "#0f172a", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>{item.value}</p>
                                    </div>
                                    {item.verified
                                        ? <CheckCircleIcon style={{ color: "#22c55e", fontSize: 16, flexShrink: 0 }} />
                                        : <WarningAmberIcon style={{ color: "#f59e0b", fontSize: 16, flexShrink: 0 }} />
                                    }
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Security Settings */}
                    <div>
                        <p style={{ color: "#64748b", fontSize: 12, fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px" }}>
                            Security Settings
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {securityItems.map((s, i) => (
                                <button key={i} onClick={s.action} style={{
                                    background: "#ffffff", border: "1px solid rgba(59,130,246,0.08)",
                                    boxShadow: "0 2px 12px rgba(59,130,246,0.02)", borderRadius: 14,
                                    padding: "13px 14px", display: "flex", alignItems: "center", gap: 12,
                                    width: "100%", textAlign: "left", cursor: "pointer"
                                }}>
                                    <div style={{
                                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                                        background: `${s.color}0a`, border: `1px solid ${s.color}1e`,
                                        color: s.color, display: "flex", alignItems: "center", justifyContent: "center"
                                    }}>
                                        {s.icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <p style={{ color: "#334155", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>{s.label}</p>
                                        <p style={{ color: "#64748b", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>{s.sub}</p>
                                    </div>
                                    <ChevronRightIcon style={{ color: "#94a3b8", fontSize: 18, flexShrink: 0 }} />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Transaction History */}
                    <div>
                        <p style={{ color: "#64748b", fontSize: 12, fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px" }}>
                            Transaction History
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {historyItems.map((h, i) => (
                                <button key={i} onClick={h.action} style={{
                                    background: "#ffffff", border: "1px solid rgba(59,130,246,0.08)",
                                    boxShadow: "0 2px 12px rgba(59,130,246,0.02)", borderRadius: 14,
                                    padding: "13px 14px", display: "flex", alignItems: "center", gap: 12,
                                    width: "100%", textAlign: "left", cursor: "pointer"
                                }}>
                                    <div style={{
                                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                                        background: `${h.color}0a`, border: `1px solid ${h.color}1e`,
                                        color: h.color, display: "flex", alignItems: "center", justifyContent: "center"
                                    }}>
                                        {h.icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <p style={{ color: "#334155", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>{h.label}</p>
                                        <p style={{ color: "#64748b", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>{h.sub}</p>
                                    </div>
                                    <ChevronRightIcon style={{ color: "#94a3b8", fontSize: 18, flexShrink: 0 }} />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Customer Support & Socials */}
                    {activeSocials.length > 0 && (
                        <div>
                            <p style={{ color: "#64748b", fontSize: 12, fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px" }}>
                                Customer Support & Socials
                            </p>
                            <div style={{
                                background: "#ffffff", border: "1px solid rgba(59,130,246,0.08)",
                                boxShadow: "0 4px 18px rgba(59,130,246,0.04)", borderRadius: 16,
                                padding: 16, display: "flex", flexDirection: "column", gap: 12
                            }}>
                                <p style={{ color: "#64748b", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: "0 0 4px", lineHeight: 1.5 }}>
                                    Need help or want to stay updated? Connect with us on our official channels.
                                </p>
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    {activeSocials.map((social) => {
                                        const Icon = social.icon;
                                        const link = site_settings?.site_settings?.[social.key];
                                        return (
                                            <a
                                                key={social.key}
                                                href={link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    flex: "1 1 120px", display: "flex", alignItems: "center", gap: 10,
                                                    padding: "10px 12px", borderRadius: 12, cursor: "pointer", outline: "none",
                                                    background: "var(--neutral-100)",
                                                    border: "1.5px solid var(--border-color)",
                                                    color: "var(--text-main)",
                                                    transition: "all 0.2s ease",
                                                    textDecoration: "none"
                                                }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.borderColor = social.color;
                                                    e.currentTarget.style.background = `${social.color}0a`;
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.borderColor = "var(--border-color)";
                                                    e.currentTarget.style.background = "var(--neutral-100)";
                                                }}
                                            >
                                                <div style={{
                                                    width: 32, height: 32, borderRadius: 8,
                                                    background: `${social.color}15`,
                                                    color: social.color, display: "flex", alignItems: "center", justifyContent: "center",
                                                    flexShrink: 0
                                                }}>
                                                    <Icon size={18} />
                                                </div>
                                                <span style={{ fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
                                                    {social.name}
                                                </span>
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Sign out */}
                    <button
                        onClick={() => dispatch(authActions.onLogout())}
                        style={{
                            borderRadius: 14, padding: "13px 0",
                            background: "rgba(239,68,68,0.05)", color: "#ef4444",
                            border: "1px solid rgba(239,68,68,0.12)",
                            fontSize: 13, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                            cursor: "pointer", display: "flex", alignItems: "center",
                            justifyContent: "center", gap: 8, width: "100%"
                        }}
                    >
                        <LogoutIcon style={{ fontSize: 16 }} />
                        Sign Out
                    </button>

                    {/* ─── Password Modal ─── */}
                    {passwordModal && (
                        <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                            <div onClick={() => setPasswordModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", backdropFilter: "blur(4px)" }} />
                            <div style={{ position: "relative", width: "100%", maxWidth: 380, background: "#ffffff", borderRadius: 20, padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" }}>
                                <p style={{ color: "#0f172a", fontSize: 16, fontWeight: 800, fontFamily: "'Syne',sans-serif", marginBottom: 20 }}>Change Password</p>
                                <form onSubmit={handlePasswordChange} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                    <div>
                                        <label style={{ color: "#334155", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", display: "block", marginBottom: 6 }}>New Password</label>
                                        <div style={{ position: "relative" }}>
                                            <input type={showPassword ? 'text' : 'password'} value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                placeholder="Enter new password" style={inputStyle} />
                                            <button type="button" onClick={() => setShowPassword(!showPassword)}
                                                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#64748b", cursor: "pointer" }}>
                                                {showPassword ? "🙈" : "👁"}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label style={{ color: "#334155", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", display: "block", marginBottom: 6 }}>Confirm Password</label>
                                        <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Confirm new password" style={inputStyle} />
                                    </div>
                                    <div>
                                        <label style={{ color: "#334155", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", display: "block", marginBottom: 6 }}>PIN</label>
                                        <input type={showPin ? 'text' : 'password'} value={withdrawalPin}
                                            onChange={(e) => setWithdrawalPin(e.target.value)}
                                            maxLength={4} placeholder="4-digit PIN" style={inputStyle} />
                                    </div>
                                    <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                                        <button type="button" onClick={() => setPasswordModal(false)} style={{
                                            flex: 1, borderRadius: 12, padding: "11px 0",
                                            border: "1px solid var(--border-color)", background: "var(--neutral-100)",
                                            color: "var(--text-muted)", fontSize: 13, fontFamily: "'DM Sans',sans-serif", cursor: "pointer"
                                        }}>Cancel</button>
                                        <button type="submit" disabled={loading} style={{
                                            flex: 1, borderRadius: 12, padding: "11px 0", border: "none",
                                            background: loading ? "rgba(59,130,246,0.2)" : "linear-gradient(135deg,#3b82f6,#2563eb)",
                                            color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: "'Syne',sans-serif", cursor: "pointer"
                                        }}>
                                            {loading ? 'Updating...' : 'Update Password'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* ─── Phone Modal ─── */}
                    {phoneModal && (
                        <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                            <div onClick={() => setPhoneModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", backdropFilter: "blur(4px)" }} />
                            <div style={{ position: "relative", width: "100%", maxWidth: 380, background: "#ffffff", borderRadius: 20, padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" }}>
                                <p style={{ color: "#0f172a", fontSize: 16, fontWeight: 800, fontFamily: "'Syne',sans-serif", marginBottom: 20 }}>Update Phone Number</p>
                                <form onSubmit={handlePhoneChange} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                    <div>
                                        <label style={{ color: "#334155", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", display: "block", marginBottom: 6 }}>Country</label>
                                        <CountrySelector
                                            value={country}
                                            onChange={(countryId) => handleCountryChange(countryId)}
                                            onFocus={() => setCountryFocused(true)}
                                            onBlur={() => setCountryFocused(false)}
                                            focused={countryFocused}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ color: "#334155", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", display: "block", marginBottom: 6 }}>Phone Number</label>
                                        <input type="text" value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            placeholder={`${phoneCode} XXX XXX XXX`} style={inputStyle} />
                                    </div>
                                    <div>
                                        <label style={{ color: "#334155", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", display: "block", marginBottom: 6 }}>PIN</label>
                                        <input type={showPin ? 'text' : 'password'} value={withdrawalPin}
                                            onChange={(e) => setWithdrawalPin(e.target.value)}
                                            maxLength={4} placeholder="4-digit PIN" style={inputStyle} />
                                    </div>
                                    <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                                        <button type="button" onClick={() => setPhoneModal(false)} style={{
                                            flex: 1, borderRadius: 12, padding: "11px 0",
                                            border: "1px solid var(--border-color)", background: "var(--neutral-100)",
                                            color: "var(--text-muted)", fontSize: 13, fontFamily: "'DM Sans',sans-serif", cursor: "pointer"
                                        }}>Cancel</button>
                                        <button type="submit" disabled={loading} style={{
                                            flex: 1, borderRadius: 12, padding: "11px 0", border: "none",
                                            background: loading ? "rgba(34,197,94,0.2)" : "linear-gradient(135deg,#22c55e,#16a34a)",
                                            color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: "'Syne',sans-serif", cursor: "pointer"
                                        }}>
                                            {loading ? 'Updating...' : 'Update Phone'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </Section>
        </SiteWide>
    );
}

export default PersonalInformation;